import React from 'react';
import { ScheduleOutlined } from '@ant-design/icons';
import './TimeCouldSave.css';
import CircularLoader from '../../componenets/CircularLoader';
import axios from 'axios';
import Cookies from 'js-cookie';
/**
 * `TimeCouldSave` is a React component that calculates and displays the potential time savings if the 
 * selected clinician in selected practice was to fully use the service.
 * It fetches this data from an API upon component mount or when the props change, and displays a loading
 * indicator while fetching. The component gracefully handles errors and displays the fetched time savings.
 *
 * Utilizes Ant Design icons for decorative purposes and custom CSS for styling.
 *
 * @param {Object} props - Component props.
 * @param {string} props.practice - The practice identifier for filtering the data.
 * @param {string} props.clinician - The clinician identifier for filtering the data.
 */
class TimeCouldSave extends React.Component {
  /**
   * Constructs the TimeCouldSave component with default state.
   * @param {Object} props - The props passed to the component.
   * 
   * @state {Object} state - The internal state of the component.
   * @state {number|null} state.timeSaved - The calculated time that could be saved, initially null.
   * @state {boolean} state.isLoading - Indicates whether the data is currently being fetched.
   */
  constructor(props) {
    super(props);
    this.state = {
      timeSaved: null,
      isLoading: false,
    };
  }

  /**
   * React lifecycle method called immediately after the component is mounted.
   * It triggers the initial data fetch.
   */
  componentDidMount() {
    this.fetchData();
  }

  /**
   * Called after the component updates to fetch new data if the practice and clinician props have changed.
   * @param {Object} prevProps - Previous props for comparison.
   */
  componentDidUpdate(prevProps) {
    if (prevProps.practice !== this.props.practice || prevProps.clinician !== this.props.clinician) {
      this.fetchData();
    }
  }

  /**
   * Fetches the potential time savings data from the specified API endpoint. Updates the component's
   * state with the new data or sets an error state if the fetch fails.
   */
  fetchData() {
    this.setState({ isLoading: true });

    const url = `${process.env.REACT_APP_BASE_URL}users/you_could_save?practice=${this.props.practice}&clinician=${this.props.clinician}`;

    const token = axios.defaults.headers.common.Authorization
    const config = {
      headers: {
        "Authorization": `${token}`
      }
    };

    axios.get(url, config)
      .then(response => {
        this.setState({ timeSaved: response.data.value, isLoading: false });
      })
      .catch(error => {
        console.error('Error fetching data:', error.message);
        this.setState({ isLoading: false, timeSaved: 0 });
      });

  }

  /**
   * Renders the component displaying the potential time savings or a loading indicator
   * while the data is being fetched.
   * 
   * @returns {React.ReactElement} The rendered component.
   */
  render() {
    const { timeSaved, isLoading } = this.state;

    return (
      <div className="could-container could-fixed-size-card">
        <ScheduleOutlined className="could-background-icon could-left-icon" />
        <div className="could-content">
          <div className="could-header">You could save</div>
          {isLoading ? (
            <CircularLoader />
          ) : (
            <div className="could-count">{timeSaved !== null ? timeSaved : 'Loading...'} hours</div>
          )}
          <div className="could-unit">with full use of Medicube</div>
        </div>
        <ScheduleOutlined className="could-background-icon could-right-icon" />
      </div>
    );
  }
}

export default TimeCouldSave;
